import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ApiService } from "./api.service";
import { LocalStorageService } from "./local-storage.service";
import { StorageKeys } from "../constants/app-constants";
import { PermissionConstants } from "../constants/permission-constants";

export interface PermissionConditionSet {
  permissionIds: string[];
  type?: 'any' | 'all' | string;
}

@Injectable({
  providedIn: "root"
})
export class PermissionService {
  private readonly _permissions$ = new BehaviorSubject<Set<string>>(new Set());

  constructor(
    private apiService: ApiService,
    private localStorageService: LocalStorageService
  ) {
  }

  get permissions$() {
    return this._permissions$.asObservable();
  }

  hasPermission(permission: string): boolean {
    return this._permissions$.value.has(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    const current = this._permissions$.value;
    return permissions.some(p => current.has(p));
  }

  hasAllPermissions(permissions: string[]): boolean {
    const current = this._permissions$.value;
    return permissions.every(p => current.has(p));
  }

  evaluatePermissionsForLoggedInUser(permissionConditionSet: PermissionConditionSet) {
    if (!permissionConditionSet) return true;

    const permissionConditionType = permissionConditionSet.type || "any";

    return permissionConditionType === "any" ?
      this.hasAnyPermission(permissionConditionSet.permissionIds) :
      this.hasAllPermissions(permissionConditionSet.permissionIds);
  }

  getPermissionsForUser(userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiService.getPermissionsIdsForUser(userId).subscribe({
        next: (res: any) => {
          const permissions = res.data || [];
          this.setPermissions(permissions);
          resolve();
        },
        error: (err: any) => {
          console.error("Failed to fetch user permissions:", err);
          reject(err);
        }
      });
    });
  }

  setPermissions(permissions: string[]): void {
    const permissionSet = new Set(permissions);
    this._permissions$.next(permissionSet);
    this.localStorageService.setItem(StorageKeys.USER_PERMISSIONS, JSON.stringify(permissions));
  }

  loadPermissionsFromStorage(): void {
    const stored = this.localStorageService.getItem(StorageKeys.USER_PERMISSIONS);
    if (stored && typeof stored === "string") {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this._permissions$.next(new Set(parsed));
        }
      } catch (e) {
        console.warn("Invalid permission data in storage");
      }
    }
  }

  clearPermissions(): void {
    this._permissions$.next(new Set());
    this.localStorageService.removeItem(StorageKeys.USER_PERMISSIONS);
  }
}
